import { produce } from 'immer';
import { keyBy, map, mapValues, omit, uniq, get } from 'lodash';

import createReducer from 'shared/utils/createReducer';
import { LOGOUT } from 'modules/auth/actions/logout';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { ARCHIVED } from 'modules/queues/constants/queueEnforcementTypes';
import { USER_UPDATED } from 'modules/common/actions/onUserUpdates';
import { ADD_SERVICE_CONFIG_TO_SCOPE } from 'modules/queues/common/actions/onServiceConfigUpdates';
import { ADD_GROUP_APPOINTMENT_TO_SCOPE } from 'modules/queues/common/actions/onGroupAppointmentsUpdates';
import {
  ADD_APPOINTMENT_TO_LIST,
  REMOVE_APPOINTMENT_FROM_LIST,
  UPDATE_EXISTING_APPOINTMENT,
} from 'modules/queues/common/actions/appointmentsListActions';
import { FETCH_ARCHIVED_APPOINTMENTS, FETCH_MORE_ARCHIVED_APPOINTMENTS } from '../actions/fetchArchivedAppointments';

const defaultState = {
  status: requestsStatuses.default,
  fetchMoreStatus: requestsStatuses.default,
  entities: {},
  byId: [],
};

export default createReducer(defaultState, {
  [FETCH_ARCHIVED_APPOINTMENTS.pending]: state => ({
    ...state,
    entities: {},
    byId: [],
    status: requestsStatuses.pending,
  }),
  [FETCH_ARCHIVED_APPOINTMENTS.failure]: state => ({
    ...state,
    status: requestsStatuses.failure,
  }),
  [FETCH_ARCHIVED_APPOINTMENTS.success]: (state, { payload: { edges } }) => ({
    status: requestsStatuses.success,
    byId: map(edges, 'appointment.appointmentId'),
    entities: keyBy(map(edges, 'appointment'), 'appointmentId'),
  }),
  [FETCH_MORE_ARCHIVED_APPOINTMENTS.pending]: (state) => ({
    ...state,
    fetchMoreStatus: requestsStatuses.pending,
  }),

  [FETCH_MORE_ARCHIVED_APPOINTMENTS.failure]: (state) => ({
    ...state,
    fetchMoreStatus: requestsStatuses.failure,
  }),

  [FETCH_MORE_ARCHIVED_APPOINTMENTS.success]: (state, { payload: { edges } }) => ({
    ...state,
    fetchMoreStatus: requestsStatuses.success,
    byId: uniq(state.byId.concat(map(edges, 'appointment.appointmentId'))),
    entities: {
      ...state.entities,
      ...keyBy(map(edges, 'appointment'), 'appointmentId'),
    },
  }),

  [ADD_APPOINTMENT_TO_LIST]: (state, { payload: { listQueueType, appointment, position } }) => {
    if (listQueueType !== ARCHIVED) return state;

    const appointmentId = appointment.appointmentId;
    const byId = [...state.byId];
    byId.splice(position, 0, appointmentId);

    return {
      ...state,
      byId,
      entities: {
        ...state.entities,
        [appointmentId]: appointment,
      },
    };
  },

  [UPDATE_EXISTING_APPOINTMENT]: (state, { payload: { listQueueType, appointment, currentPosition, updatedPosition } }) => {
    if (listQueueType !== ARCHIVED) return state;

    const appointmentId = appointment.appointmentId;
    const byId = [...state.byId];

    if (currentPosition === updatedPosition) {
      byId.splice(currentPosition, 1, appointmentId);
    } else {
      byId.splice(currentPosition, 1);
      byId.splice(updatedPosition, 0, appointmentId);
    }

    return {
      ...state,
      byId,
      entities: {
        ...state.entities,
        [appointmentId]: appointment,
      },
    };
  },

  [REMOVE_APPOINTMENT_FROM_LIST]: (state, { payload: { listQueueType, appointmentId } }) => {
    if (listQueueType !== ARCHIVED) return state;

    return {
      ...state,
      byId: state.byId.filter(id => id !== appointmentId),
      entities: omit(state.entities, appointmentId),
    };
  },

  [ADD_SERVICE_CONFIG_TO_SCOPE]: (state, { payload: { serviceId, config } }) => {
    if (!state.byId.length) return state;

    return {
      ...state,
      entities: mapValues(state.entities, appointment => {
        if (appointment.service.serviceId === serviceId) {
          return produce(appointment, draft => { draft.service.config = config; });
        }

        return appointment;
      }),
    };
  },

  [ADD_GROUP_APPOINTMENT_TO_SCOPE]: (state, { payload }) => {
    if (!state.byId.length) return state;

    return {
      ...state,
      entities: mapValues(state.entities, (appointment) => {
        if (
          get(appointment, 'group.groupId') === payload.groupId
          && get(appointment, 'status') === payload.appointments.find(i => i.appointmentId === appointment.appointmentId).status
        ) {
          return produce(appointment, draft => { draft.group = payload; });
        }

        return appointment;
      }),
    };
  },

  [USER_UPDATED]: (state, { payload: { userId, profile } }) => {
    if (!state.byId.length) return state;

    return {
      ...state,
      entities: mapValues(state.entities, (appointment) => {
        if (
          appointment.participants.some(participant => participant.user.userId === userId)
          || appointment.subjects.some(subject => subject.user.userId === userId)
        ) {
          const newAppointment = produce(appointment, draft => {
            draft.participants.map(participant => {
              if (participant.user.userId === userId) {
                participant.user.profile = profile;
              }

              return participant;
            });

            draft.subjects.map(subject => {
              if (subject.user.userId === userId) {
                subject.user.profile = profile;
              }

              return subject;
            });
          });
          return newAppointment;

        }

        return appointment;
      }),
    };
  },

  [LOGOUT.failure]: () => defaultState,
  [LOGOUT.success]: () => defaultState,
});
